import { useQuery } from "@tanstack/react-query";
import ApiService from "services/api.service";

export interface LeadStatsFeatureFlagState {
  enabled: boolean;
  envName: string;
  description?: string;
}

export interface LeadStatsFeatureFlags {
  metadata: LeadStatsFeatureFlagState;
  automation: LeadStatsFeatureFlagState;
  bulkRuns: LeadStatsFeatureFlagState;
  internalNotifications: LeadStatsFeatureFlagState;
  documentFields: LeadStatsFeatureFlagState;
}

const disabledFlag: LeadStatsFeatureFlagState = {
  enabled: false,
  envName: "",
};

export const disabledLeadStatsFeatureFlags: LeadStatsFeatureFlags = {
  metadata: disabledFlag,
  automation: disabledFlag,
  bulkRuns: disabledFlag,
  internalNotifications: disabledFlag,
  documentFields: disabledFlag,
};

// Shared query so every consumer (nav drawer, workflow trigger editor, settings,
// etc.) resolves to one cache entry instead of each refetching on mount. The
// flags don't change during a session, so once any consumer has loaded them the
// rest read instantly — which is what removes the brief "flag-off" flicker when
// opening a workflow.
export const leadStatsFeatureFlagsQuery = () => ({
  queryKey: ["leadstats", "feature-flags"],
  queryFn: async () => {
    const { data } = await ApiService.get<{ flags: LeadStatsFeatureFlags }>({
      url: "/leadstats/feature-flags",
    });
    return {
      ...disabledLeadStatsFeatureFlags,
      ...(data?.flags ?? {}),
    };
  },
  staleTime: Infinity,
  gcTime: Infinity,
});

const useLeadStatsFeatureFlags = () => {
  const { data, isFetched } = useQuery({
    ...leadStatsFeatureFlagsQuery(),
    // Keep `flags` defined (everything disabled) during the very first fetch,
    // without writing that placeholder into the cache or marking it fetched.
    placeholderData: disabledLeadStatsFeatureFlags,
  });

  return { flags: data ?? disabledLeadStatsFeatureFlags, isLoaded: isFetched };
};

export default useLeadStatsFeatureFlags;
